<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useCategoryStore } from '../../stores';

const props = defineProps<{
  selectedCategories: string[];
}>();

const emit = defineEmits<{
  'update:selectedCategories': [string[]];
}>();

const categoryStore = useCategoryStore();

// Estado para el desplazamiento con mouse
const isDragging = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);
const scrollContainer = ref<HTMLElement | null>(null);

// Estado para indicadores de scroll
const showLeftIndicator = ref(false);
const showRightIndicator = ref(false);

// Manejadores de eventos para el desplazamiento con mouse
const handleMouseDown = (e: MouseEvent) => {
  if (!scrollContainer.value) return;
  
  isDragging.value = true;
  startX.value = e.pageX - scrollContainer.value.offsetLeft;
  scrollLeft.value = scrollContainer.value.scrollLeft;
  
  // Cambiar el cursor
  scrollContainer.value.style.cursor = 'grabbing';
  scrollContainer.value.style.userSelect = 'none';
};

const handleMouseMove = (e: MouseEvent) => {
  if (!isDragging.value || !scrollContainer.value) return;

  e.preventDefault();
  const x = e.pageX - scrollContainer.value.offsetLeft;
  const walk = (x - startX.value) * 2; // Multiplicador para ajustar la velocidad
  scrollContainer.value.scrollLeft = scrollLeft.value - walk;
};

const handleMouseUp = () => {
  isDragging.value = false;
  if (scrollContainer.value) {
    scrollContainer.value.style.cursor = 'grab';
    scrollContainer.value.style.removeProperty('user-select');
  }
};

// Verificar si hay contenido oculto
const checkScrollIndicators = () => {
  if (!scrollContainer.value) return;
  
  const { scrollLeft, scrollWidth, clientWidth } = scrollContainer.value;
  showLeftIndicator.value = scrollLeft > 0;
  showRightIndicator.value = scrollLeft + clientWidth < scrollWidth;
};

// Scroll manual
const scroll = (direction: 'left' | 'right') => {
  if (!scrollContainer.value) return;
  
  const scrollAmount = 200; // Cantidad de píxeles a desplazar
  const targetScroll = scrollContainer.value.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
  
  scrollContainer.value.scrollTo({
    left: targetScroll,
    behavior: 'smooth'
  });
};

// Observar cambios en el scroll
watch(() => categoryStore.categoryOrder, checkScrollIndicators);

// Resetear filtros
const resetFilters = () => {
  emit('update:selectedCategories', []);
};

// Helpers para categorías
const toggleCategory = (categoryId: string) => {
  emit('update:selectedCategories', [categoryId]);
};

defineExpose({ resetFilters });
</script>

<template>
  <div class="bg-white rounded-lg shadow-sm border border-slate-200">
    <div class="relative">
      <!-- Gradient shadows and scroll buttons -->
      <div 
        v-show="showLeftIndicator"
        class="absolute left-0 top-0 bottom-0 flex items-center z-20"
      >
        <div class="absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-white to-transparent pointer-events-none" />
        <button
          @click="scroll('left')"
          class="h-full px-2 text-slate-400 hover:text-slate-600 bg-gradient-to-r from-white to-transparent"
          title="Desplazar a la izquierda"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>
      
      <div 
        v-show="showRightIndicator"
        class="absolute right-0 top-0 bottom-0 flex items-center z-20"
      >
        <div class="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />
        <button
          @click="scroll('right')"
          class="h-full px-2 text-slate-400 hover:text-slate-600 bg-gradient-to-l from-white to-transparent"
          title="Desplazar a la derecha"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      
      <!-- Scrollable tabs container -->
      <div 
        ref="scrollContainer"
        class="overflow-x-auto scrollbar-hide cursor-grab relative"
        @mousedown="handleMouseDown"
        @mousemove="handleMouseMove"
        @mouseup="handleMouseUp"
        @mouseleave="handleMouseUp"
        @scroll="checkScrollIndicators"
      >
        <nav class="flex whitespace-nowrap border-b border-slate-200 min-w-full select-none">
          <button
            v-for="categoryId in categoryStore.categoryOrder"
            :key="categoryId"
            @click="toggleCategory(categoryId)"
            :class="[
              'px-6 py-3 text-sm font-medium transition-colors duration-200 border-b-2',
              selectedCategories.includes(categoryId)
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-slate-600 hover:text-slate-800 hover:border-slate-300'
            ]"
          >
            {{ categoryStore.categories[categoryId] }}
          </button>
          <button
            v-if="selectedCategories.length > 0"
            @click="resetFilters"
            class="px-6 py-3 text-sm font-medium text-slate-600 hover:text-slate-800 hover:border-slate-300 border-b-2 border-transparent ml-auto"
          >
            Ver Todas
          </button>
        </nav>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Hide scrollbar but keep functionality */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Prevent text selection while dragging */
.select-none {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
</style>